import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDocumentTitle } from 'usehooks-ts';
import {
  MdArrowBack,
  MdEdit,
  MdDelete,
  MdOutlineLocalOffer,
} from 'react-icons/md';
import { PricePerMileModal } from '@components/PricePerMileModal';
import { Button } from '../../tailwindComponents/Button';
import { useAuthHttp } from '../../hooks';
import { FLEET_GET, FLEET_IMAGES_URL } from '../../constants';
import { VehicleSummary as Vehicle, fleetMapping } from '../FleetMaster/types';

const fleetImagePlaceholder = '/images/vehicle_placeholder.png';

export function VehicleDetails() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const authHttp = useAuthHttp();
  const [vehicle, setVehicle] = useState<Vehicle | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isPricePerMileOpen, setIsPricePerMileOpen] = useState(false);

  useDocumentTitle(
    vehicle ? `FLOX - ${vehicle.model}` : 'FLOX - Vehicle Details'
  );

  const fetchVehicle = async (isStillOnThisVehicle: () => boolean) => {
    setIsLoading(true);
    try {
      const { data } = await authHttp.post(FLEET_GET, {
        pagination: {
          itemsPerPage: 100,
        },
      });

      if (data?.vehicles) {
        const foundVehicle = data.vehicles.find(
          (v: Vehicle) => v.vehicleId === id
        );
        if (foundVehicle && isStillOnThisVehicle()) {
          setVehicle(foundVehicle);
        }
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
    } finally {
      if (isStillOnThisVehicle()) setIsLoading(false);
    }
  };

  useEffect(() => {
    let isStillOnThisVehicle = true;
    setVehicle(null);
    fetchVehicle(() => isStillOnThisVehicle);
    return () => {
      isStillOnThisVehicle = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const hasActualImage = vehicle?.images && vehicle.images.length > 0;
  const imageUrl = hasActualImage
    ? `${FLEET_IMAGES_URL}/${vehicle.images[0].imageLocation}/small/${vehicle.images[0].remoteName}`
    : fleetImagePlaceholder;

  const handleEdit = () => {
    navigate(`/vehicle/${id}/edit`);
  };

  if (isLoading) {
    return (
      <div className='flex items-center justify-center min-h-screen'>
        <div className='text-gray-500'>Loading vehicle details...</div>
      </div>
    );
  }

  if (!vehicle) {
    return (
      <div className='flex flex-col items-center justify-center min-h-screen gap-4'>
        <div className='text-gray-500'>Vehicle not found</div>
        <Button
          label='Back to Fleet'
          intent='primary'
          onClick={() => navigate('/fleet-master')}
        />
      </div>
    );
  }

  return (
    <>
      <PricePerMileModal
        vehicleType={vehicle.vehicleType}
        isOpen={isPricePerMileOpen}
        onClose={() => setIsPricePerMileOpen(false)}
      />
      {/* Custom header with back button */}
      <div className='bg-white border-b border-gray-200 px-10 py-4 sticky top-16 z-40'>
        <div className='flex items-center justify-between'>
          <div className='flex items-center gap-4'>
            <button
              type='button'
              onClick={() => navigate('/fleet-master')}
              className='p-2 hover:bg-gray-100 rounded-lg transition-colors'
            >
              <MdArrowBack className='w-5 h-5 text-gray-600' />
            </button>
            <div>
              <h1 className='text-2xl font-bold text-gray-900'>
                {vehicle.model}
              </h1>
              <p className='text-sm text-gray-500'>
                # {vehicle.registrationNumber}
              </p>
            </div>
          </div>
          <div className='flex items-center gap-2'>
            <Button
              label='Price per mile'
              intent='secondary'
              startIcon={<MdOutlineLocalOffer />}
              size='sm'
              onClick={() => setIsPricePerMileOpen(true)}
            />
            <Button
              label='Edit'
              intent='secondary'
              startIcon={<MdEdit />}
              size='sm'
              onClick={handleEdit}
            />
            {/* <button
              type='button'
              className='p-2.5 border border-red-200 text-red-600 hover:bg-red-50 rounded-lg transition-colors'
            >
              <MdDelete className='w-5 h-5' />
            </button> */}
          </div>
        </div>
      </div>

      <main className='p-8'>
        {/* Image Section */}
        <div className='mb-8'>
          <div className='w-full max-w-2xl bg-gray-50 rounded-lg overflow-hidden border border-gray-200'>
            <img
              src={imageUrl}
              alt={vehicle.model}
              className={`w-full h-[300px] ${
                hasActualImage ? 'object-cover' : 'object-contain p-8'
              }`}
            />
          </div>
        </div>

        {/* Vehicle Details Section */}
        <div className='mb-8'>
          <h2 className='text-lg font-semibold text-gray-900 mb-4'>
            Vehicle details
          </h2>
          <div className='grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6'>
            <div>
              <p className='text-base font-semibold text-gray-900'>
                {fleetMapping[vehicle.vehicleType] || vehicle.vehicleType}
              </p>
              <p className='text-sm text-gray-500'>Fleet category</p>
            </div>
            <div>
              <p className='text-base font-semibold text-gray-900'>
                {vehicle.vehicleCategory || '-'}
              </p>
              <p className='text-sm text-gray-500'>Trailer type</p>
            </div>
            <div>
              <p className='text-base font-semibold text-gray-900'>
                {vehicle.fuelType || '-'}
              </p>
              <p className='text-sm text-gray-500'>Fuel type</p>
            </div>
            <div>
              <p className='text-base font-semibold text-gray-900'>
                {vehicle.emissionStandard || '-'}
              </p>
              <p className='text-sm text-gray-500'>Emission standard</p>
            </div>
            <div>
              <p className='text-base font-semibold text-gray-900'>
                Trailer features
              </p>
              <div className='flex flex-col gap-1 mt-1'>
                {vehicle.specialEquipment &&
                vehicle.specialEquipment.length > 0 ? (
                  vehicle.specialEquipment.map((equipment) => (
                    <div
                      key={equipment}
                      className='flex items-center gap-1.5 text-sm text-gray-600'
                    >
                      <svg
                        className='w-4 h-4 text-gray-400'
                        viewBox='0 0 20 20'
                        fill='currentColor'
                      >
                        <path
                          fillRule='evenodd'
                          d='M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z'
                          clipRule='evenodd'
                        />
                      </svg>
                      {equipment}
                    </div>
                  ))
                ) : (
                  <span className='text-sm text-gray-400'>None</span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Annual Mileage */}
        <div className='mb-8'>
          <p className='text-xl font-semibold text-gray-900'>
            {vehicle.averageAnnualMileage?.toLocaleString() || '-'}{' '}
            {vehicle.averageAnnualMileageUnits || 'km'}
          </p>
          <p className='text-sm text-gray-500'>Average annual mileage</p>
        </div>

        {/* Capacity Section */}
        <div>
          <h2 className='text-lg font-semibold text-gray-900 mb-4'>Capacity</h2>
          <div className='grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6'>
            <div>
              <p className='text-xl font-semibold text-gray-900'>
                {vehicle.vehicleCapacity?.maxPallets || '-'}
              </p>
              <p className='text-sm text-gray-500'>Standard pallets</p>
            </div>
            <div>
              <p className='text-xl font-semibold text-gray-900'>
                {vehicle.vehicleCapacity?.maxKilograms?.toLocaleString() || '-'}{' '}
                kg
              </p>
              <p className='text-sm text-gray-500'>Load weight</p>
            </div>
            <div>
              <p className='text-xl font-semibold text-gray-900'>
                {vehicle.vehicleCapacity?.maxCapacityDimensions?.width || '-'}{' '}
                {vehicle.vehicleCapacity?.maxCapacityDimensions
                  ?.dimensionsUnitOfMeasure || 'm'}
              </p>
              <p className='text-sm text-gray-500'>Width</p>
            </div>
            <div>
              <p className='text-xl font-semibold text-gray-900'>
                {vehicle.vehicleCapacity?.maxCapacityDimensions?.height || '-'}{' '}
                {vehicle.vehicleCapacity?.maxCapacityDimensions
                  ?.dimensionsUnitOfMeasure || 'm'}
              </p>
              <p className='text-sm text-gray-500'>Height</p>
            </div>
            <div>
              <p className='text-xl font-semibold text-gray-900'>
                {vehicle.vehicleCapacity?.maxCapacityDimensions?.length || '-'}{' '}
                {vehicle.vehicleCapacity?.maxCapacityDimensions
                  ?.dimensionsUnitOfMeasure || 'm'}
              </p>
              <p className='text-sm text-gray-500'>Length</p>
            </div>
          </div>
        </div>
      </main>
    </>
  );
}
